import React, { ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import { AnimatePresence, motion, type PanInfo } from 'framer-motion';
import { useIsDesktop } from '../../hooks/useIsDesktop';

type DesktopMaxWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '5xl';
type MobileVariant = 'fullscreen' | 'bottomSheet';

interface MobileModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  /**
   * Pinned action bar rendered outside the scroll area, flush to the bottom of
   * the panel. Use this rather than a `sticky` footer inside `children`: a
   * sticky element is capped by the scroll container's own padding, so it never
   * truly reaches the bottom edge.
   */
  footer?: ReactNode;
  title?: string;
  showCloseButton?: boolean;
  /** @deprecated Prefer `mobileVariant`. Kept for back-compat. */
  fullScreen?: boolean;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  twoColumnLayout?: boolean;
  rightSideContent?: ReactNode;
  /**
   * Mobile presentation:
   *  - `fullscreen` (default): the modal fills the viewport top-to-bottom.
   *  - `bottomSheet`: slides up from the bottom with rounded top corners,
   *    a drag handle, and drag-down-to-dismiss. Best for pickers and
   *    short-form content where the page underneath should remain visible.
   */
  mobileVariant?: MobileVariant;
  /** Max-width preset for the desktop centred modal. Default 'md'. */
  desktopMaxWidth?: DesktopMaxWidth;
  /** Close when the scrim is tapped. Default true. */
  closeOnScrimClick?: boolean;
  /** Close when Escape is pressed. Default true. */
  closeOnEsc?: boolean;
}

const DESKTOP_WIDTH_CLASS: Record<DesktopMaxWidth, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '5xl': 'max-w-5xl',
};

const SHEET_SPRING = { type: 'spring' as const, damping: 32, stiffness: 320, mass: 0.9 };
const FADE_TRANSITION = { duration: 0.22, ease: [0.16, 1, 0.3, 1] as const };
const SCALE_TRANSITION = { duration: 0.32, ease: [0.16, 1, 0.3, 1] as const };

const MobileModal: React.FC<MobileModalProps> = ({
  isOpen,
  onClose,
  children,
  footer,
  title,
  showCloseButton = true,
  fullScreen = true,
  className = '',
  contentClassName = '',
  headerClassName = '',
  twoColumnLayout = false,
  rightSideContent,
  mobileVariant,
  desktopMaxWidth = 'md',
  closeOnScrimClick = true,
  closeOnEsc = true,
}) => {
  // `mobileVariant` wins; otherwise fall back to legacy `fullScreen` flag.
  const resolvedVariant: MobileVariant =
    mobileVariant ?? (fullScreen ? 'fullscreen' : 'bottomSheet');

  const isDesktop = useIsDesktop(twoColumnLayout ? 1024 : 640);

  // Lock body scroll while open (and restore on close/unmount).
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  // Esc to close.
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeOnEsc, onClose]);

  const handleScrimClick = () => {
    if (closeOnScrimClick) onClose();
  };

  // ─── Desktop ──────────────────────────────────────────────────────────────
  const renderDesktop = () => (
    <motion.div
      key="modal-desktop"
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-md flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={FADE_TRANSITION}
      onClick={handleScrimClick}
    >
      {twoColumnLayout ? (
        <motion.div
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.95, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 8 }}
          transition={SCALE_TRANSITION}
          className={`relative bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex overflow-hidden ${className}`}
        >
          {showCloseButton && (
            <button
              onClick={onClose}
              className="absolute top-6 left-6 p-2 hover:bg-gray-100/80 rounded-full transition-colors z-10"
              aria-label="Close"
            >
              <X size={18} className="text-gray-600 hover:text-gray-900" />
            </button>
          )}
          <div className="flex-1 flex flex-col overflow-y-auto">
            <div className="p-12">{children}</div>
          </div>
          {rightSideContent && (
            <div className="w-1/2 relative overflow-hidden">{rightSideContent}</div>
          )}
        </motion.div>
      ) : (
        <motion.div
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.95, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 8 }}
          transition={SCALE_TRANSITION}
          className={`relative bg-white rounded-3xl shadow-2xl border border-gray-100 w-full ${DESKTOP_WIDTH_CLASS[desktopMaxWidth]} max-h-[90vh] flex flex-col overflow-hidden ${className}`}
        >
          {showCloseButton && (
            <button
              onClick={onClose}
              className="absolute top-5 right-5 p-2 hover:bg-gray-100/80 rounded-full transition-colors z-10"
              aria-label="Close"
            >
              <X size={18} className="text-gray-600 hover:text-gray-900" />
            </button>
          )}
          <div className="flex flex-col h-full">
            {title && (
              <div className={`flex-shrink-0 pt-8 pb-4 px-8 ${headerClassName}`}>
                <h2 className="text-xl font-bold text-gray-900">{title}</h2>
              </div>
            )}
            <div
              className={`flex-1 overflow-y-auto px-8 pb-8 ${!title ? 'pt-12' : ''} ${contentClassName}`}
              style={{ maxHeight: 'calc(90vh - 120px)' }}
            >
              {children}
            </div>
            {footer && <div className="flex-shrink-0">{footer}</div>}
          </div>
        </motion.div>
      )}
    </motion.div>
  );

  // ─── Mobile: bottom sheet ─────────────────────────────────────────────────
  const renderMobileBottomSheet = () => {
    const handleDragEnd = (_: unknown, info: PanInfo) => {
      // Confident drag-down or fast downward fling closes the sheet.
      if (info.offset.y > 120 || info.velocity.y > 600) {
        onClose();
      }
    };

    return (
      <motion.div
        key="modal-mobile-sheet"
        className="fixed inset-0 z-[60] flex items-end justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={FADE_TRANSITION}
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-md"
          onClick={handleScrimClick}
        />
        <motion.div
          role="dialog"
          aria-modal="true"
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.4 }}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={SHEET_SPRING}
          className={`relative w-full bg-white rounded-t-3xl shadow-[0_-8px_32px_rgba(0,0,0,0.16)] max-h-[88vh] flex flex-col overflow-hidden touch-pan-y ${className}`}
        >
          {/* Drag handle */}
          <div className="flex-shrink-0 flex justify-center pt-2.5 pb-1.5">
            <span className="w-10 h-1.5 rounded-full bg-gray-300" />
          </div>

          {(title || showCloseButton) && (
            <div
              className={`flex-shrink-0 flex items-center justify-between px-5 pt-2 pb-3 ${headerClassName}`}
            >
              {title ? (
                <h2 className="text-[17px] font-bold text-gray-900 truncate">{title}</h2>
              ) : (
                <span />
              )}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="p-2 -mr-2 rounded-full hover:bg-gray-100 active:scale-95 transition"
                  aria-label="Close"
                >
                  <X size={18} className="text-gray-500" />
                </button>
              )}
            </div>
          )}

          <div className={`flex-1 overflow-y-auto overscroll-contain ${contentClassName}`}>
            {children}
          </div>

          <div
            className="flex-shrink-0"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {footer}
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // ─── Mobile: full-screen (legacy default) ─────────────────────────────────
  const renderMobileFullscreen = () => (
    <motion.div
      key="modal-mobile-fullscreen"
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={FADE_TRANSITION}
      onClick={handleScrimClick}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        transition={SCALE_TRANSITION}
        className={`fixed inset-0 flex flex-col bg-white ${className}`}
      >
        <div
          className={`flex items-center ${title ? 'justify-between' : 'justify-start'} p-6 pb-4 ${headerClassName}`}
        >
          {showCloseButton && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100/80 rounded-full transition-colors"
              aria-label="Close"
            >
              <X size={20} className="text-gray-600 hover:text-gray-900" />
            </button>
          )}
          {title && (
            <h2 className="text-xl font-bold text-gray-900 flex-1 text-center pr-12">
              {title}
            </h2>
          )}
        </div>

        <div className={`flex-1 overflow-y-auto ${contentClassName}`}>
          <div className="px-6 pb-6">{children}</div>
        </div>

        <div
          className="flex-shrink-0"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {footer}
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <AnimatePresence>
      {isOpen
        ? isDesktop
          ? renderDesktop()
          : resolvedVariant === 'bottomSheet'
          ? renderMobileBottomSheet()
          : renderMobileFullscreen()
        : null}
    </AnimatePresence>
  );
};

export default MobileModal;
